/**
 * AuditReportPanel.tsx
 * 
 * Epic 54: Lender Audit Report UI - Score & Evidence Dashboard
 * 
 * This panel provides a comprehensive audit view of a member's VeraScore,
 * including factor breakdown, score trends, and evidence links.
 * 
 * Features:
 * - Factor Contribution BarChart (54.2)
 * - Score Trend AreaChart (54.3)
 * - Evidence drilldown links
 * - Audit timestamp and run_id display
 */

import { FC, useState } from 'react';
import type { FinancialHealthStats } from '../../types/financialStats';
import { FactorContributionChart } from '../charts/FactorContributionChart';
import { format, parseISO } from 'date-fns';

interface AuditReportPanelProps {
  /** Array of financial stats for the member */
  stats: FinancialHealthStats[];
  /** Member UUID for audit trail */
  memberUuid: string;
  /** Optional: algo_run_id for deep linking to evidence */
  runId?: string;
  /** Optional: Show debug information */
  showDebug?: boolean;
  /** Optional: Callback when factor is clicked for drilldown */
  onFactorDrilldown?: (factorKey: string) => void;
  /** Optional: Selected month to display (Option 3: Hybrid Date Selection) */
  selectedMonth?: string | null;
}

type TabType = 'overview' | 'factors';

export const AuditReportPanel: FC<AuditReportPanelProps> = ({
  stats,
  memberUuid,
  runId,
  showDebug = false,
  onFactorDrilldown,
  selectedMonth,
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('overview');

  // Get stats for selected month, or fallback to latest
  const sortedStats = [...stats].sort(
    (a, b) => new Date(a.month).getTime() - new Date(b.month).getTime()
  );
  // Option 3: Hybrid Date Selection - use selectedMonth if provided
  const latestStats = selectedMonth
    ? stats.find(s => s.month === selectedMonth) || sortedStats[sortedStats.length - 1]
    : sortedStats[sortedStats.length - 1];

  if (!latestStats) {
    return (
      <div className="p-4 text-center text-slate-500">
        No financial data available for audit report.
      </div>
    );
  }

  // Format calculation timestamp
  const calcTimestamp = latestStats.calculation_timestamp
    ? format(parseISO(latestStats.calculation_timestamp), 'MMM d, yyyy h:mm a')
    : 'N/A';

  const tabs: { key: TabType; label: string; icon: string }[] = [
    { key: 'overview', label: 'Overview', icon: '📊' },
    { key: 'factors', label: 'Factor Breakdown', icon: '📈' },
  ];

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 bg-gradient-to-r from-brand-primary/5 to-brand-secondary/5 border-b border-slate-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-slate-800">Audit Report</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Calculated: {calcTimestamp}
              {runId && <span className="ml-2 text-slate-400">| Run: {runId.slice(0, 8)}...</span>}
            </p>
            {/* Demo2 fast-fix: algo_run_evidence has no rows for Demo CU members, so this
                panel is reconstructed from lender_member_summary projection fields. Surface
                that fact instead of implying real evidence-row provenance. */}
            <p className="text-[11px] text-amber-600 mt-0.5 italic">
              Demo report reconstructed from lender summary fields.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-3xl font-bold text-brand-primary">
              {latestStats.verascore?.toFixed(0) ?? '—'}
            </span>
            <span className="text-xs text-slate-500 uppercase tracking-wide">VeraScore</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-200">
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors ${
              activeTab === tab.key
                ? 'text-brand-primary border-b-2 border-brand-primary bg-brand-primary/5'
                : 'text-slate-600 hover:text-slate-800 hover:bg-slate-50'
            }`}
          >
            <span className="mr-1.5">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="p-4">
        {activeTab === 'overview' && (
          <div className="space-y-4">
            {/* Quick Stats Row */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-slate-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-brand-primary">
                  {latestStats.verascore?.toFixed(0) ?? '—'}
                </div>
                <div className="text-xs text-slate-500">Current Score</div>
              </div>
              <div className="bg-slate-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-slate-700">
                  {sortedStats.length}
                </div>
                <div className="text-xs text-slate-500">Months of Data</div>
              </div>
              <div className="bg-slate-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-slate-700">
                  {latestStats.calculation_version
                    || import.meta.env.VITE_ALGORITHM_VERSION
                    || '—'}
                </div>
                <div className="text-xs text-slate-500">Algorithm Version</div>
              </div>
            </div>
            {/* Factor Chart */}
            <div>
              <h4 className="text-sm font-medium text-slate-700 mb-2">Factor Contributions</h4>
              <FactorContributionChart
                stats={latestStats}
                showDebug={showDebug}
                onFactorClick={onFactorDrilldown}
              />
            </div>
          </div>
        )}

        {activeTab === 'factors' && (
          <FactorContributionChart
            stats={latestStats}
            showDebug={showDebug}
            onFactorClick={onFactorDrilldown}
          />
        )}
      </div>

      {/* Footer with audit links */}
      {showDebug && (
        <div className="px-4 py-2 bg-slate-50 border-t border-slate-200 text-xs text-slate-500">
          Member: {memberUuid.slice(0, 8)}... | Source: {latestStats.source_snapshot_id?.slice(0, 8) ?? 'N/A'}
        </div>
      )}
    </div>
  );
};

export default AuditReportPanel;

